import { Injectable, Injector } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ApplicationContext } from "../models/application-context.model";
import { ApplicationContextService } from "./application-context.service";
import { AuthenticationService } from "./auth.service";
import {
  findPreferredUserCompany,
  getUserCompanyId,
  getVisibleUserCompanies,
  resolveAdminOrganizationContext,
} from "../utils/admin-company.util";
import { shouldSkipPreferredCompanyAutoSelect } from "../utils/admin-launch-context.util";

@Injectable({ providedIn: "root" })
export class AdminCompanyAutoSelectService {
  constructor(
    private readonly _authService: AuthenticationService,
    private readonly _injector: Injector,
    private readonly _router: Router
  ) {}

  private resetApplicationContext(): void {
    this._injector.get(ApplicationContextService).resetContext();
  }

  /**
   * When the session lacks org context but the user belongs to the preferred org,
   * switch tokens without showing the company picker.
   */
  tryAutoSelectPreferredCompany(
    context: ApplicationContext,
    options?: { navigateTo?: string; navigate?: boolean }
  ): Observable<boolean> {
    if (shouldSkipPreferredCompanyAutoSelect()) {
      return of(false);
    }

    const decision = resolveAdminOrganizationContext({
      companyId: context.currentlyLoggedInUser?.companyId,
      userCompanies: context.userCompanies,
    });
    if (decision !== "select-company") {
      return of(false);
    }

    const preferred = findPreferredUserCompany(
      context.userCompanies,
      environment.preferredAdminCompanyId
    );
    if (!preferred) {
      return of(false);
    }

    const currentCompanyId =
      context.currentlyLoggedInUser?.companyId?.toLowerCase();
    const preferredCompanyId = getUserCompanyId(preferred)?.toLowerCase();
    if (
      currentCompanyId &&
      preferredCompanyId &&
      currentCompanyId === preferredCompanyId
    ) {
      return of(false);
    }

    return this.switchToCompany(preferred).pipe(
      tap((switched) => {
        if (switched && options?.navigate !== false) {
          void this._router.navigateByUrl(options?.navigateTo ?? "/");
        }
      }),
      catchError(() => of(false))
    );
  }

  /** Auto-scope when the user belongs to exactly one visible organization. */
  tryAutoSelectSingleCompany(
    context: ApplicationContext,
    options?: { navigateTo?: string; navigate?: boolean }
  ): Observable<boolean> {
    const visible = getVisibleUserCompanies(context.userCompanies);
    if (visible.length !== 1) {
      return of(false);
    }
    if (context.currentlyLoggedInUser?.companyId?.trim()) {
      return of(false);
    }

    return this.switchToCompany(visible[0]).pipe(
      tap((switched) => {
        if (switched && options?.navigate !== false) {
          void this._router.navigateByUrl(options?.navigateTo ?? "/");
        }
      }),
      catchError(() => of(false))
    );
  }

  switchToCompany(userCompany: { userId: string; company?: { id: string }; companyId?: string }): Observable<boolean> {
    const companyId = userCompany.companyId || userCompany.company?.id;
    if (!companyId) {
      return of(false);
    }
    return this.switchToCompanyIds(userCompany.userId, companyId);
  }

  switchToCompanyIds(userId: string, companyId: string): Observable<boolean> {
    return this._authService.getAuthTokenForCompany(userId, companyId).pipe(
      tap((response) => {
        const accessToken = (response?.accessToken || "").trim();
        if (!accessToken) {
          throw new Error("Identity session required to select a company");
        }
        this._authService.persistAuthData({
          accessToken,
          refreshToken: (response?.refreshToken || "").trim(),
        });
        this.resetApplicationContext();
      }),
      map(() => true),
      catchError(() => of(false))
    );
  }
}
